
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  getClassificationScores,
  getNormalScores,
  getBalancedEquationScores,
  getPrinciplesScores
} from '../api';

import { navigation } from '../data';
import SubPagesLayout from '../../UI/layout/subPages';
import { LeaderboardOverall } from '../../UI/instructor/leaderboard/overall';

import '../styles.css';

export default function DecisionInsightsOverall() {
  const navigate = useNavigate();

  const [filterBy, setFilterBy] = useState('Accuracy');
  const [data, setData] = useState(null);

  const filterByOptions = ['Accuracy', 'Points'];

  const { data: classificationScores } = useQuery({
    queryKey: ['classificationScoresOverall'],
    queryFn: async () => getClassificationScores(),
    refetchOnWindowFocus: false,
  });

  const { data: normalScores } = useQuery({
    queryKey: ['normalScoresOverall'],
    queryFn: async () => getNormalScores(),
    refetchOnWindowFocus: false,
  });

  const { data: balancedEquationScores } = useQuery({
    queryKey: ['equationScoresOverall'],
    queryFn: async () => getBalancedEquationScores(),
    refetchOnWindowFocus: false,
  });

  const { data: principlesScores } = useQuery({
    queryKey: ['principlesScoresOverall'],
    queryFn: async () => getPrinciplesScores(),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if(classificationScores && normalScores && balancedEquationScores && principlesScores) {
      formatLeaderboardData();
    }
  }, [classificationScores, normalScores, balancedEquationScores, principlesScores, filterBy]);

  const calculateAverage = (data, param) => {
    if (!data || data.length === 0) return 0;

    let sum = 0;
    let count = 0;

    data.forEach(record => {
      const value = record[param];
      if (value !== null && value !== undefined) {
        sum += value;
        count++;
      }
    });

    return count > 0 ? sum / count : 0;
  };

  const formatLeaderboardData = () => {    
    let wholeLeaderboardData = [];

    const classificationScore = filterBy === 'Accuracy' ? calculateAverage(classificationScores, 'total_accuracy') : calculateAverage(classificationScores, 'total_points');
    const classificationFormattedScore = filterBy === 'Accuracy' ? (classificationScore * 100).toFixed(2) + '%' : classificationScore;
    
    const normalScore = filterBy === 'Accuracy' ? calculateAverage(normalScores, 'total_accuracy') : calculateAverage(normalScores, 'total_points');
    const normalFormattedScore = filterBy === 'Accuracy' ? (normalScore * 100).toFixed(2) + '%' : normalScore;
    
    const balancedEquationScore = filterBy === 'Accuracy' ? calculateAverage(balancedEquationScores, 'total_accuracy') : calculateAverage(balancedEquationScores, 'total_points');
    const balancedEquationFormattedScore = filterBy === 'Accuracy' ? (balancedEquationScore * 100).toFixed(2) + '%' : balancedEquationScore;
    
    const principlesScore = filterBy === 'Accuracy' ? calculateAverage(principlesScores, 'total_accuracy') : calculateAverage(principlesScores, 'total_points');
    const principlesFormattedScore = filterBy === 'Accuracy' ? (principlesScore * 100).toFixed(2) + '%' : principlesScore;
    
    wholeLeaderboardData = [
      {
        name: 'Classification',
        score: classificationScore,
        formattedScore: classificationFormattedScore,
        backgroundColor: 'rgba(85, 180, 188, 0.8)'
      },
      {
        name: 'Normal Balances',
        score: normalScore,
        formattedScore: normalFormattedScore,
        backgroundColor: '#FDD38A'
      },
      {
        name: 'Double Entry',
        score: balancedEquationScore,
        formattedScore: balancedEquationFormattedScore,
        backgroundColor: 'rgba(169, 141, 246, 0.8)'
      },
      {
        name: 'The Principles',
        score: principlesScore,
        formattedScore: principlesFormattedScore,
        backgroundColor: '#70A1FF'
      },
    ];

    setData(wholeLeaderboardData);
  }

  const handleFilterClick = (selectedFilterBy) => {
    setFilterBy(selectedFilterBy);
  }

  const maxScore = data ? data.reduce((max, item) => (item.score > max ? item.score : max), -Infinity) : 0;

  const handleNextButtonClick = () => {
    navigate('/insights/decision-insights/classifying-normal' );
  }

  return (
    <SubPagesLayout
      navigation={navigation}
      contentClassName="decision-insights-content-container"
      onSecondButtonClick={handleNextButtonClick}
    >
      <h1>Insights | Overall</h1>
      <h2>Overall</h2>
      <p>The chart below is an indicator of how well you understand accounting basics.</p>
      <LeaderboardOverall
        title="Performance"
        data={data}
        filterBy={filterBy}
        filterByOptions={filterByOptions}
        onFilterByClick={handleFilterClick}
        maxScore={maxScore}
      />
      <p><span className="red"><b>Note:</b></span> The chart will continuously update until the deadline has passed on all assignments.</p>
    </SubPagesLayout>
  );
}
